import { Environment, SessionId, SystemId } from '../interfaces';
import * as localStorage from '../utility/local.storage';
import * as sessionStorage from '../utility/session.storage';
import * as storageKeys from '../utility/session.storage.keys';

// Global webpack variables for server mimic mode
declare const WEBPACK_SERVER_MIMIC_MODE: boolean;
declare const WEBPACK_SERVER_MIMIC_BASE_PATH: string;

export function isDbtEnvironment() {
  return document.location.hostname.endsWith('-dbt.ascendon.tv');
}

export function isDevelopmentEnvironment() {
  return (
    isDbtEnvironment() || isEdgeEnvironment() || isLocalDevelopmentEnvironment()
  );
}

export function isEdgeEnvironment() {
  return document.location.hostname.includes('edge') && isDbtEnvironment();
}

export function isLocalDevelopmentEnvironment() {
  return document.location.hostname === 'localhost';
}

/**
 * Check if we're running in server mimic mode
 * Server mimic mode simulates production-like paths in development
 */
export function isServerMimicMode() {
  return (
    typeof WEBPACK_SERVER_MIMIC_MODE !== 'undefined' &&
    WEBPACK_SERVER_MIMIC_MODE
  );
}

/**
 * Get the base path for server mimic mode
 * @returns The base path (e.g., "/customercare2") or empty string if not in server mimic mode
 */
export function getServerMimicBasePath() {
  return typeof WEBPACK_SERVER_MIMIC_BASE_PATH !== 'undefined'
    ? WEBPACK_SERVER_MIMIC_BASE_PATH
    : '';
}

export function establishSessionFromLocalStorage() {
  const newSessionId = localStorage.read(
    storageKeys.SESSION_NEW_SESSION_ID_KEY
  );

  if (newSessionId) {
    sessionStorage.write(storageKeys.SESSION_SESSION_ID_KEY, newSessionId);
    localStorage.remove(storageKeys.SESSION_NEW_SESSION_ID_KEY);
  }
}

export function readSessionStorage() {
  const sessionId = sessionStorage.read(
    storageKeys.SESSION_SESSION_ID_KEY
  ) as unknown as SessionId;

  const environment = sessionStorage.read(
    storageKeys.ENVIRONMENT_KEY
  ) as Environment;

  const systemId = sessionStorage.read(
    storageKeys.SESSION_SYSTEM_ID_KEY
  ) as unknown as SystemId;

  return { sessionId, environment, systemId };
}

/**
 * Extract current module path from the URL
 *
 * **Note**: This function is intended for production and server mimic modes
 * where modules have distinct path prefixes (e.g., /customercare2/, /authentication/).
 * In regular local development mode, this may return unexpected results.
 *
 * @returns Module path (e.g., "/configuration" from "/configuration/some/page")
 *
 * @example
 * // In production or server mimic mode:
 * // URL: https://invision.qa1.ascendon.tv/configuration/appointments
 * // Returns: "/configuration"
 *
 * // URL: http://localhost:3000/customercare2/customer/123
 * // Returns: "/customercare2"
 */
export function getCurrentModulePath(): string {
  return document.location.pathname.split('/').slice(0, 2).join('/');
}

/**
 * Navigate to the application landing page with environment-aware routing
 *
 * **Note**: This function handles different deployment modes appropriately:
 * - Regular local dev: Goes to root (/)
 * - Server mimic mode: Goes to current module root
 * - Production: Goes to current module root
 *
 * @example
 * // Usage in error handlers, reload buttons, etc.
 * goToAppLandingPage();
 */
export function goToAppLandingPage(): void {
  if (isLocalDevelopmentEnvironment() && !isServerMimicMode()) {
    // Regular local development: go to root
    window.location.href = '/';
  } else {
    // Server mimic mode OR production/staging: reload to current module's root
    window.location.href =
      getCurrentModulePath() || getServerMimicBasePath() || '/';
  }
}
